import React from 'react';
import { Transaction, TagMap } from '../types';
import { formatDate } from '../utils/dateUtils';
import { ReactComponent as ExcludedIcon } from '../assets/icons/ExcludedFromAccountingIcon.svg';
import CurrencyDisplay from './AmountDisplay';
import AccountIcon from './AccountIcon';
import { BsFillTagFill } from 'react-icons/bs';
import { GiCrownCoin } from 'react-icons/gi';
import Badge from './Badge';
import { normalizeTagKey, UNTAGGED_KEY } from '../utils/tagHelpers';
import { useDisplayPrivacy } from '../contexts/DisplayPrivacyContext';
import { randomizeDisplayAmount } from '../utils/amountRandomizer';

interface TransactionCardProps {
  transaction: Transaction;
  tagMap: TagMap;
  onTagClick?: (transaction: Transaction, event: React.MouseEvent) => void;
  onCardClick?: (transaction: Transaction) => void;
  showNote?: boolean;
  className?: string;
}

function TransactionCard({ transaction, tagMap, onTagClick, onCardClick, showNote = false, className }: TransactionCardProps) {
  const { randomizeAmounts } = useDisplayPrivacy();
  const normalizedTagKey = normalizeTagKey(transaction.tag);
  const currentTagName = normalizedTagKey === UNTAGGED_KEY
    ? undefined
    : (tagMap[normalizedTagKey]?.name ?? transaction.tag ?? undefined);
  const isCashAccountTransaction = transaction.account?.name === 'Cash';
  const originalAmountForDisplay =
    transaction.originalAmount !== undefined && transaction.originalAmount !== null
      ? (randomizeAmounts ? randomizeDisplayAmount(transaction.originalAmount) : transaction.originalAmount)
      : null;
  const noteForDisplay = transaction.notes?.trim();

  return (
    <div
      role={onCardClick ? "button" : undefined}
      tabIndex={onCardClick ? 0 : undefined}
      className={`bg-secondary p-3 flex-shrink-0 w-full text-left transition-colors ${onCardClick ? 'cursor-pointer' : 'cursor-default'} ${className}`}
      onClick={() => onCardClick && onCardClick(transaction)}
      onKeyDown={(e) => {
        if (onCardClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onCardClick(transaction);
        }
      }}
    >
      <div className="flex justify-between items-center mb-2 pointer-events-none">
        <span className="text-muted-foreground text-sm font-semibold flex-grow mr-2 truncate">
          {transaction.counterParty || transaction.description}
        </span>
        <span className="text-muted-foreground text-sm font-semibold flex-shrink-0">
          {formatDate(transaction.transactionDate)}
        </span>
      </div>

      <hr className="border-t border-border my-2 pointer-events-none" />

      <div className="grid grid-cols-1 items-center mt-1 pointer-events-none overflow-hidden">
        <div className="col-start-1 row-start-1 self-stretch flex flex-col justify-center min-w-max justify-self-start z-10 bg-gradient-to-r from-secondary via-secondary/95 to-transparent pr-8">
          <CurrencyDisplay
            amount={transaction.amount}
            className='text-xl font-bold text-foreground'
            smallRupeeSymbol={true}
            type={transaction.type}
          />
          {originalAmountForDisplay !== null && transaction.currencyCode && transaction.currencyCode !== 'INR' && (
            <span className="text-xs text-muted-foreground mt-1">
              Original: {originalAmountForDisplay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {transaction.currencyCode}
            </span>
          )}
          {transaction.possibleDuplicate && (
            <span className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              Possible duplicate
            </span>
          )}
        </div>
        <div className="col-start-1 row-start-1 flex shrink-0 items-center space-x-2 whitespace-nowrap justify-self-end pointer-events-auto z-0">
          <Badge
            as="button"
            variant="default"
            size="sm"
            onClick={(e) => onTagClick && onTagClick(transaction, e)}
            disabled={!onTagClick}
            className={`h-8 min-w-8 shrink-0 gap-1 text-secondary-foreground ${onTagClick ? 'transition-colors hover:bg-input' : 'cursor-default'
              }`}
          >
            {currentTagName ? (
              <>
                <BsFillTagFill size={14} className="text-muted-foreground" />
                <span className="whitespace-nowrap">{currentTagName}</span>
              </>
            ) : (
              <BsFillTagFill size={14} className="text-muted-foreground" />
            )}
          </Badge>
          {transaction.excludeFromAccounting && (
            <span className="text-muted-foreground flex items-center pointer-events-none" title="Excluded from accounting">
              <ExcludedIcon className="h-6 w-6" />
            </span>
          )}
          {transaction.account && (
            <div className="flex items-center pointer-events-none" title={`Account ID: ${transaction.account.id}`}>
              {isCashAccountTransaction ? (
                <GiCrownCoin className="w-4 h-4 text-foreground" />
              ) : (
                <AccountIcon account={transaction.account} className="w-4 h-4" />
              )}
            </div>
          )}
        </div>
      </div>
      {showNote && noteForDisplay && (
        <div className="mt-2 pointer-events-none">
          <p className="text-xs text-muted-foreground truncate" title={noteForDisplay}>
            {noteForDisplay}
          </p>
        </div>
      )}
    </div>
  );
}

export default TransactionCard; 
